import { acceptHMRUpdate, defineStore } from 'pinia';

import { useAlertsStore } from '@tallo/design-system';

import { AvailabilitySlot, InvestorAvailability, NewAvailabilitySlotDto } from '../interfaces/availability.interface';
import investorAvailabilityService from '../services/availability.service';

export const useAvailabilityStore = defineStore({
  id: 'availability',
  state: (): {
    investorAvailability: InvestorAvailability | null;
    loaded: boolean;
    isLoading: boolean;
    currentPropertyId: string | null;
  } => ({
    investorAvailability: null,
    loaded: false,
    isLoading: false,
    currentPropertyId: null,
  }),
  getters: {
    getAvailabilitySlots: (state) => state.investorAvailability?.availabilitySlots || [],
    getInvestorAvailabilityId(): string | undefined {
      return this.investorAvailability?.id;
    },
    availabilityLoaded(): boolean {
      return this.loaded;
    },
    availabilityLoading(): boolean {
      return this.isLoading;
    },
    getCurrentPropertyId(): string | null {
      return this.currentPropertyId;
    },
    getShowingDuration(): number | undefined {
      return this.investorAvailability?.showingDurationInMinutes;
    },
  },
  actions: {
    setInvestorAvailability(investorAvailability: InvestorAvailability): void {
      this.investorAvailability = investorAvailability;
      this.loaded = true;
      this.isLoading = false;
    },

    pushNewSlotToExistingAvailability(availability: AvailabilitySlot): void {
      this.investorAvailability?.availabilitySlots.push(availability);
    },

    async loadAvailability(propertyId: string): Promise<void> {
      if (this.currentPropertyId !== propertyId || !this.loaded) {
        try {
          this.isLoading = true;

          if (this.currentPropertyId !== propertyId) {
            this.$reset();
            this.isLoading = true;
          }

          this.currentPropertyId = propertyId;

          const investorAvailability = await investorAvailabilityService.getPropertyAvailability(propertyId);
          this.setInvestorAvailability(investorAvailability);
        } catch (error) {
          console.error(error);
          this.isLoading = false;
        }
      }
    },

    async updateShowingDuration(durationInMinutes: number): Promise<void> {
      const alertsStore = useAlertsStore();

      try {
        await investorAvailabilityService.updateShowingDuration(this.getInvestorAvailabilityId!, durationInMinutes);
        if (this.investorAvailability) {
          this.investorAvailability.showingDurationInMinutes = durationInMinutes;
        }
      } catch (error) {
        console.error(error);
        alertsStore.error('Unable to save changes. Please, refresh the page and try again.');
        throw error;
      }
    },

    async addSlot(availability: NewAvailabilitySlotDto, availabilityId: string): Promise<AvailabilitySlot | undefined> {
      const alertsStore = useAlertsStore();

      try {
        const newAvailability = await investorAvailabilityService.createAvailabilitySlot(availability, availabilityId);
        this.pushNewSlotToExistingAvailability(newAvailability);
        return newAvailability;
      } catch (error) {
        console.error(error);
        alertsStore.error('Unable to save changes. Please, refresh the page and try again.');
      }
    },

    async updateSlot(availability: AvailabilitySlot): Promise<AvailabilitySlot | undefined> {
      const alertsStore = useAlertsStore();

      try {
        const updatedAvailability = await investorAvailabilityService.updateAvailabilitySlot(availability);
        const index = this.investorAvailability!.availabilitySlots.findIndex(
          ({ id }: AvailabilitySlot) => id === updatedAvailability.id,
        );
        this.investorAvailability!.availabilitySlots[index] = updatedAvailability;
        return updatedAvailability;
      } catch (error) {
        console.error(error);
        alertsStore.error('Unable to save changes. Please, refresh the page and try again.');
      }
    },

    async deleteSlots(ids: string[]): Promise<void> {
      const alertsStore = useAlertsStore();

      try {
        await investorAvailabilityService.deleteAvailabilitySlots(ids).then(() => {
          this.investorAvailability!.availabilitySlots = this.investorAvailability!.availabilitySlots.filter(
            (slot) => !ids.includes(slot.id),
          );
        });
      } catch (error) {
        console.error(error);
        alertsStore.error('Unable to save changes. Please, refresh the page and try again.');
      }
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAvailabilityStore, import.meta.hot));
}
